import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { Card, Button, Form, Alert, Modal } from "react-bootstrap";
import ReactQuill, { Quill } from "react-quill";
import ImageResize from "quill-image-resize-module-react";
import ImageDropAndPaste from "quill-image-drop-and-paste";
import "react-quill/dist/quill.snow.css";
import {
  applyFloatToSelectedImage,
  contentClipboardMatchers,
  getQuillHtml,
  IMAGE_FLOAT_LEFT_STYLE,
} from "../../utils/quillContentSetup";
import {
  getAllBerita,
  createBerita,
  updateBerita,
  uploadBeritaImage,
  uploadContentFile,
  getBeritaUploadImageUrl,
  normalizeContentImageUrls,
  getApiErrorMessage,
  BASE_URL,
} from "../../services/api";
import { getStoredUser } from "../../utils/authStorage";
import "./ContentManagement.css";
import "./BeritaManagement.css";
import "./Dashboard.css";

Quill.register("modules/imageResize", ImageResize);
Quill.register("modules/imageDropAndPaste", ImageDropAndPaste);

const WelcomeManagement = () => {
  const [welcomeData, setWelcomeData] = useState({
    id: null,
    judul: "Selamat Datang",
    judul_en: "Welcome",
    isiberita: "",
    isiberita_en: "",
    foto: "",
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: "", message: "" });

  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const quillRef = useRef(null);
  const quillRefEn = useRef(null);
  const activeQuillRef = useRef(null);
  const imageInsertModeRef = useRef("normal");

  const [htmlModal, setHtmlModal] = useState({ visible: false, value: "" });
  const htmlSourceQuillRef = useRef(null);

  const toUploadFile = (fileOrImageData) => {
    if (fileOrImageData instanceof File) return fileOrImageData;
    if (fileOrImageData?.toFile) return fileOrImageData.toFile();
    return fileOrImageData;
  };

  const getActiveQuill = () =>
    quillRef.current?.getEditor?.() || activeQuillRef.current;

  const isEmptyQuillHtml = (html) => {
    if (!html) return true;
    if (/<img\b/i.test(html)) return false;
    const text = html
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/gi, " ")
      .trim();
    return !text;
  };

  const syncEditorHtmlToState = useCallback((quill) => {
    if (!quill?.root) return "";
    const html = normalizeContentImageUrls(quill.root.innerHTML);
    setWelcomeData((prev) => ({ ...prev, isiberita: html }));
    return html;
  }, []);

  const uploadAndInsertImage = useCallback(
    async (fileOrImageData, quill, mode = "normal") => {
      if (!fileOrImageData || !quill) return;

      const file = toUploadFile(fileOrImageData);
      if (!file?.type?.startsWith("image/")) {
        setAlert({
          show: true,
          type: "danger",
          message: "Hanya file gambar yang diperbolehkan untuk fitur ini.",
        });
        return;
      }

      try {
        const formData = new FormData();
        formData.append("image", file);
        const response = await uploadBeritaImage(formData);

        if (!response.data.success) {
          setAlert({
            show: true,
            type: "danger",
            message: response.data.message || "Gagal mengunggah gambar.",
          });
          return;
        }

        const url = getBeritaUploadImageUrl(response.data.data);
        const range = quill.getSelection(true);
        const insertIndex = range ? range.index : quill.getLength();

        if (mode === "layout") {
          const layoutHtml = `<p class="ql-align-justify"><img src="${url}" class="content-img-left" style="${IMAGE_FLOAT_LEFT_STYLE}" />Ketik teks di sini di sebelah kanan foto...</p><p><br></p>`;
          quill.clipboard.dangerouslyPasteHTML(insertIndex, layoutHtml, "user");
          quill.setSelection(insertIndex + 1, 0, "user");
        } else {
          quill.insertEmbed(insertIndex, "image", url, "user");
          quill.setSelection(insertIndex + 1, 0, "user");
        }

        syncEditorHtmlToState(quill);
      } catch (err) {
        console.error("Image upload error:", err);
        setAlert({
          show: true,
          type: "danger",
          message:
            err.response?.data?.message ||
            "Gagal mengunggah gambar. Pastikan tipe dan ukuran file sesuai.",
        });
      }
    },
    [syncEditorHtmlToState],
  );

  const handleImageSelected = useCallback(
    async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      e.target.value = "";

      const quill = activeQuillRef.current;
      const mode = imageInsertModeRef.current;
      imageInsertModeRef.current = "normal";
      await uploadAndInsertImage(file, quill, mode);
    },
    [uploadAndInsertImage],
  );

  const handleFileSelected = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = "";

    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await uploadContentFile(formData);

      if (response.data.success) {
        const url = BASE_URL + response.data.url;
        const quill = activeQuillRef.current;
        if (quill) {
          const range = quill.getSelection(true);
          const insertIndex = range ? range.index : quill.getLength();
          quill.insertText(insertIndex, file.name, "link", url, "user");
          quill.setSelection(insertIndex + file.name.length, 0, "user");
          quill.format("link", false, "user");
        }
      }
    } catch (err) {
      console.error("File upload error:", err);
      setAlert({
        show: true,
        type: "danger",
        message:
          err.response?.data?.message ||
          "Gagal mengunggah file. Pastikan tipe dan ukuran file sesuai (maks 100 MB).",
      });
    }
  }, []);

  const quillModules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          ["bold", "italic", "underline", "strike"],
          [{ color: [] }, { background: [] }],
          [{ list: "ordered" }, { list: "bullet" }],
          [{ indent: "-1" }, { indent: "+1" }],
          [{ align: [false, "center", "right", "justify"] }],
          ["link", "image", "video", "file-upload"],
          ["img-float-left", "img-float-right", "insert-side-layout"],
          ["clean", "code-block", "html-source"],
        ],
        handlers: {
          image: function () {
            activeQuillRef.current = this.quill;
            imageInsertModeRef.current = "normal";
            if (imageInputRef.current) imageInputRef.current.click();
          },
          "file-upload": function () {
            activeQuillRef.current = this.quill;
            if (fileInputRef.current) fileInputRef.current.click();
          },
          "img-float-left": function () {
            const applied = applyFloatToSelectedImage(this.quill, "left");
            if (!applied) {
              setAlert({
                show: true,
                type: "warning",
                message:
                  "Klik di dalam gambar terlebih dahulu, lalu tekan tombol Foto Kiri.",
              });
            }
          },
          "img-float-right": function () {
            const applied = applyFloatToSelectedImage(this.quill, "right");
            if (!applied) {
              setAlert({
                show: true,
                type: "warning",
                message:
                  "Klik di dalam gambar terlebih dahulu, lalu tekan tombol Foto Kanan.",
              });
            }
          },
          "insert-side-layout": function () {
            activeQuillRef.current = this.quill;
            imageInsertModeRef.current = "layout";
            if (imageInputRef.current) imageInputRef.current.click();
          },
          "html-source": function () {
            htmlSourceQuillRef.current = this.quill;
            setHtmlModal({ visible: true, value: this.quill.root.innerHTML });
          },
        },
      },
      imageDropAndPaste: {
        handler: function (_dataUrl, _type, imageData) {
          uploadAndInsertImage(imageData, this.quill, "normal");
        },
      },
      imageResize: {
        parchment: Quill.import("parchment"),
        modules: ["Resize", "DisplaySize", "Toolbar"],
      },
      clipboard: {
        matchVisual: true,
        matchers: contentClipboardMatchers,
      },
    }),
    [uploadAndInsertImage],
  );

  const handleHtmlModalClose = useCallback(() => {
    setHtmlModal({ visible: false, value: "" });
    htmlSourceQuillRef.current = null;
  }, []);

  const handleHtmlModalSave = useCallback(() => {
    const quill = htmlSourceQuillRef.current;
    if (quill) {
      quill.clipboard.dangerouslyPasteHTML(htmlModal.value);
      syncEditorHtmlToState(quill);
    }
    setHtmlModal({ visible: false, value: "" });
    htmlSourceQuillRef.current = null;
  }, [htmlModal.value, syncEditorHtmlToState]);

  const quillFormats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "background",
    "list",
    "bullet",
    "indent",
    "align",
    "link",
    "image",
    "imageFloat",
    "video",
    "code-block",
    "width",
    "height",
    "style",
  ];

  useEffect(() => {
    fetchWelcomeData();
  }, []);

  const fetchWelcomeData = async () => {
    try {
      const response = await getAllBerita();
      if (response.data.success) {
        const welcome = response.data.data.find(
          (item) => item.jenisberita === "welcome",
        );
        if (welcome) {
          setWelcomeData({
            id: welcome.id,
            judul: welcome.judul || "Selamat Datang",
            judul_en: welcome.judul_en || "Welcome",
            isiberita: normalizeContentImageUrls(welcome.isiberita || ""),
            isiberita_en: normalizeContentImageUrls(welcome.isiberita_en || ""),
            foto: welcome.foto || "",
          });
        }
      }
    } catch (error) {
      console.error("Error fetching welcome data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setWelcomeData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleEditorChange = (content) => {
    setWelcomeData((prev) => ({
      ...prev,
      isiberita: content,
    }));
  };

  const handleEditorChangeEn = (content) => {
    setWelcomeData((prev) => ({
      ...prev,
      isiberita_en: content,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setAlert({ show: false, type: "", message: "" });

    const user = getStoredUser();

    const quill = getActiveQuill();
    const quillId = quillRef.current?.getEditor?.();
    const quillEn = quillRefEn.current?.getEditor?.();
    const isiberitaHtml = normalizeContentImageUrls(
      getQuillHtml(quillId || quill) || welcomeData.isiberita || "",
    );
    const isiberitaHtmlEn = normalizeContentImageUrls(
      getQuillHtml(quillEn) || welcomeData.isiberita_en || "",
    );

    if (isEmptyQuillHtml(isiberitaHtml)) {
      setAlert({
        show: true,
        type: "warning",
        message: "Isi konten tidak boleh kosong.",
      });
      setSubmitting(false);
      return;
    }

    if (/data:image\//i.test(isiberitaHtml)) {
      setAlert({
        show: true,
        type: "warning",
        message:
          "Masih ada gambar base64 di konten. Hapus gambar tersebut, lalu sisipkan ulang lewat tombol gambar atau paste agar ter-upload ke server.",
      });
      setSubmitting(false);
      return;
    }

    try {
      const dataToSend = {
        judul: welcomeData.judul.trim(),
        ringkasan: welcomeData.judul.trim(),
        isiberita: isiberitaHtml,
        judul_en: welcomeData.judul_en?.trim() || null,
        ringkasan_en: welcomeData.judul_en?.trim() || null,
        isiberita_en: isiberitaHtmlEn || null,
        foto: welcomeData.foto || null,
        pengirim: user?.userid || "Admin",
        jenisberita: "welcome",
      };

      let response;
      if (welcomeData.id) {
        response = await updateBerita(welcomeData.id, dataToSend);
      } else {
        response = await createBerita(dataToSend);
      }

      if (response.data.success) {
        const saved = response.data.data;
        setWelcomeData((prev) => ({
          ...prev,
          id: saved?.id ?? prev.id,
          judul_en: saved?.judul_en ?? prev.judul_en,
          isiberita: isiberitaHtml,
          isiberita_en: isiberitaHtmlEn,
          foto: saved?.foto ?? prev.foto,
        }));

        setAlert({
          show: true,
          type: "success",
          message: "Welcome message berhasil disimpan!",
        });
      } else {
        setAlert({
          show: true,
          type: "danger",
          message: response.data.message || "Gagal menyimpan welcome message.",
        });
      }
    } catch (error) {
      console.error("Error saving welcome data:", error);
      setAlert({
        show: true,
        type: "danger",
        message: getApiErrorMessage(
          error,
          "Terjadi kesalahan. Silakan coba lagi.",
        ),
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="berita-management-page">
      <div
        className="page-header-banner"
        style={{
          background:
            "linear-gradient(135deg, var(--primary-color) 0%, var(--primary-hover) 50%, var(--primary-dark) 100%)",
        }}
      >
        <div className="page-header-content">
          <div className="page-header-left">
            <div className="page-header-icon">
              <i className="fas fa-home"></i>
            </div>
            <div>
              <h1 className="page-header-title">Edit Welcome Message</h1>
              <p className="page-header-subtitle">
                Edit konten Selamat Datang yang tampil di halaman utama
              </p>
            </div>
          </div>
        </div>
      </div>

      <Card className="table-card">
        <Card.Body>
          {alert.show && (
            <Alert
              variant={alert.type}
              onClose={() => setAlert({ show: false, type: "", message: "" })}
              dismissible
            >
              {alert.message}
            </Alert>
          )}

          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>
                Judul <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="judul"
                value={welcomeData.judul}
                onChange={handleChange}
                placeholder="Masukkan judul welcome message"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Isi Konten <span className="text-danger">*</span>
              </Form.Label>
              <div className="welcome-quill-editor">
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={welcomeData.isiberita}
                  onChange={(content, _delta, _source, editor) => {
                    activeQuillRef.current = editor;
                    handleEditorChange(content);
                  }}
                  onFocus={(_range, _source, editor) => {
                    activeQuillRef.current = editor;
                  }}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Tulis konten welcome message di sini..."
                />
              </div>
              <Form.Text className="text-muted">
                <strong>Layout foto + teks:</strong> gunakan tombol{" "}
                <em>Foto+Teks</em> untuk menyisipkan foto di kiri dengan teks
                rata kanan di sampingnya. Atau sisipkan gambar lalu klik{" "}
                <em>Foto Kiri</em>/<em>Foto Kanan</em>, pilih teks di
                sampingnya, lalu gunakan tombol rata kanan. Gambar bisa
                di-resize dengan menarik sudutnya.
              </Form.Text>
            </Form.Group>

            <hr className="my-4" />

            <Form.Group className="mb-3">
              <Form.Label>Judul (English)</Form.Label>
              <Form.Control
                type="text"
                name="judul_en"
                value={welcomeData.judul_en || ""}
                onChange={handleChange}
                placeholder="Enter welcome title in English"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Content (English)</Form.Label>
              <div className="welcome-quill-editor">
                <ReactQuill
                  ref={quillRefEn}
                  theme="snow"
                  value={welcomeData.isiberita_en || ""}
                  onChange={(content, _delta, _source, editor) => {
                    activeQuillRef.current = editor;
                    handleEditorChangeEn(content);
                  }}
                  onFocus={(_range, _source, editor) => {
                    activeQuillRef.current = editor;
                  }}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Write welcome content in English..."
                />
              </div>
              <Form.Text className="text-muted">
                Jika dikosongkan, halaman depan bahasa Inggris akan fallback ke
                konten bahasa Indonesia.
              </Form.Text>
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button
                type="submit"
                variant="primary"
                disabled={submitting || !welcomeData.judul?.trim()}
              >
                {submitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Menyimpan...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save me-2"></i>
                    Simpan
                  </>
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp"
        onChange={handleFileSelected}
      />
      <input
        type="file"
        ref={imageInputRef}
        style={{ display: "none" }}
        accept="image/jpeg,image/png,image/gif,image/webp"
        onChange={handleImageSelected}
      />

      <Modal
        show={htmlModal.visible}
        onHide={handleHtmlModalClose}
        size="xl"
        centered
        className="html-source-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title
            style={{ fontFamily: "Consolas, monospace", fontSize: "1rem" }}
          >
            <i className="fas fa-code me-2"></i>
            Edit HTML Source
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 0 }}>
          <div
            style={{
              background: "#f8fafc",
              padding: "6px 16px",
              fontSize: "11px",
              color: "#64748b",
              fontFamily: "Consolas, monospace",
              borderBottom: "1px solid #e2e8f0",
            }}
          >
            Edit HTML secara langsung. Klik{" "}
            <strong style={{ color: "#16a34a" }}>Terapkan</strong> untuk
            menyimpan perubahan ke editor, atau{" "}
            <strong style={{ color: "#dc2626" }}>Batal</strong> untuk membuang
            perubahan.
          </div>
          <textarea
            style={{
              width: "100%",
              height: "65vh",
              background: "#ffffff",
              color: "#1e293b",
              fontFamily: "Consolas, 'Courier New', monospace",
              fontSize: "13px",
              lineHeight: "1.6",
              border: "none",
              padding: "16px",
              resize: "none",
              outline: "none",
              display: "block",
              whiteSpace: "pre-wrap",
              overflowWrap: "break-word",
              overflowX: "hidden",
            }}
            value={htmlModal.value}
            onChange={(e) =>
              setHtmlModal((prev) => ({ ...prev, value: e.target.value }))
            }
            spellCheck={false}
          />
        </Modal.Body>
        <Modal.Footer style={{ gap: "0.5rem" }}>
          <span
            style={{
              color: "#94a3b8",
              fontSize: "12px",
              flex: 1,
              fontFamily: "Consolas, monospace",
            }}
          >
            {htmlModal.value.length} karakter
          </span>
          <Button variant="secondary" onClick={handleHtmlModalClose}>
            Batal
          </Button>
          <Button variant="success" onClick={handleHtmlModalSave}>
            <i className="fas fa-check me-2"></i>Terapkan
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default WelcomeManagement;
